import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '../../contexts/auth';
import { TutorialTour } from './tutorialTour.component';

const TUTORIAL_QUERY_PARAM = 'tutorial';

/**
 * Whoever loads the demo gets the tutorial straight after it - but the page
 * reloads in between (so every list fetches the demo's records), and the
 * request rides that reload in the address rather than in browser storage.
 * Remembered for this tab only: someone else who happened to be watching the
 * loading screen does not get it.
 */
let tutorialAfterDemoLoad = false;

export const requestTutorialAfterDemoLoad = () => {
  tutorialAfterDemoLoad = true;
};

/** Where the app goes once the demo is in. */
export const pathAfterDemoLoad = () =>
  tutorialAfterDemoLoad ? `/home?${TUTORIAL_QUERY_PARAM}=start` : '/home';

interface TutorialContextValue {
  /** Starts the tutorial from the beginning - only while there is demo data. */
  start: () => void;
}

const TutorialContext = createContext<TutorialContextValue>({ start: () => {} });

export const useTutorial = () => useContext(TutorialContext);

export const TutorialProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(0);
  const [active, setActive] = useState(false);
  // It walks through the demo's records, so there is nothing to show without them.
  const demoLoaded = currentUser?.tenant?.demo_status === 'A';

  const start = useCallback(() => {
    setRun((previous) => previous + 1);
    setActive(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(TUTORIAL_QUERY_PARAM) !== 'start') return;

    params.delete(TUTORIAL_QUERY_PARAM);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    if (demoLoaded) start();
  }, [location, navigate, demoLoaded, start]);

  useEffect(() => {
    if (!demoLoaded) setActive(false);
  }, [demoLoaded]);

  return (
    <TutorialContext.Provider value={{ start }}>
      {children}
      {active && demoLoaded && <TutorialTour key={run} onClose={() => setActive(false)} />}
    </TutorialContext.Provider>
  );
};
